import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AlertService, ConstantService } from '@app/_services';
import { MessageService, ConfirmationService } from 'primeng/api';
import { FeaturesService } from '../features.service';
import { PropertyService } from '@app/_components/property/property.service';
import { PropertyFeatureService } from '@app/_components/property/property-feature/property-feature.service';
import { PropertyFeature, PropertyFeatureAdd } from '@app/_components/property/property-feature/property-feature';
import { FeatureAdminContainerComponent } from '@app/_components/property/feature/feature-admin-container/feature-admin-container.component';

interface FeatureState {
	exists: boolean;
	status: boolean;
}

interface PropertyRow {
	property: any;
	featureMap: Map<number, FeatureState>;
}

@Component({
	selector: 'app-feature-pivot-summary',
	templateUrl: './feature-pivot-summary.component.html',
	styleUrls: ['./feature-pivot-summary.component.css'],
	providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
	changeDetection: ChangeDetectionStrategy.OnPush,
	// make column heading sticky
	styles: [`
  :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
      position: -webkit-sticky;
      position: sticky;
      top: 0px;
  }

  @media screen and (max-width: 64em) {
      :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
          top: 99px;
      }
  }
`],
    standalone: false
})
export class FeaturePivotSummaryComponent implements OnInit, OnDestroy {

	private destroy$ = new Subject<void>();

	// Composite key map for O(1) feature lookups during updates: `${shortTermRentalId}_${featureId}` → propertyFeature object
	private pfLookup = new Map<string, any>();

	@Input() showFilter = true;
	@Input() showPagination = true;
	@Input() outlineNewButton = true;

	feature: any;
	isSearching = false;
	statuses: any;

	// Pre-computed table data — one entry per property row, featureMap keyed by featureId
	tableRows: PropertyRow[] = [];

	// Pre-computed location name map for O(1) template lookups
	locationMap = new Map<number, string>();

	columns: any[] = [
		{ field: 'propertyCode', header: 'Property', width: 'auto' },
		{ field: 'locationId', header: 'Location', width: 'auto' },
		{ field: 'kbmMap', header: 'Elevator', featureId: 19, width: 'auto' },
		{ field: 'kbmMap', header: 'Pool', featureId: 2062, width: 'auto' },
		{ field: 'kbmMap', header: 'Washer', featureId: 1489, width: 'auto' },
		{ field: 'kbmMap', header: 'Tennis Court', featureId: 1963, width: 'auto' },
		{ field: 'kbmMap', header: 'Air Conditioning', featureId: 1198, width: 'auto' },
		{ field: 'kbmMap', header: 'Heater', featureId: 1219, width: 'auto' },
		{ field: 'kbmMap', header: 'Fitness', featureId: 934, width: 'auto' },
		{ field: 'kbmMap', header: 'Fireplace', featureId: 1210, width: 'auto' },
		{ field: 'kbmMap', header: 'Wifi', featureId: 1243, width: 'auto' },
		{ field: 'kbmMap', header: 'Children', featureId: 3587, width: 'auto' },
		{ field: 'kbmMap', header: 'Dishwasher', featureId: 1294, width: 'auto' },
		{ field: 'kbmMap', header: 'Beach Front', featureId: 1603, width: 'auto' },
		{ field: 'kbmMap', header: 'Beach View', featureId: 3349, width: 'auto' },
		{ field: 'kbmMap', header: 'Ocean View', featureId: 3439, width: 'auto' },
		{ field: 'kbmMap', header: 'Water or Pool View', featureId: 3481, width: 'auto' },
		{ field: 'kbmMap', header: 'Golf Course', featureId: 1561, width: 'auto' },
		{ field: 'kbmMap', header: 'Ski In/Out', featureId: 1630, width: 'auto' },
		{ field: 'kbmMap', header: 'Walk to Ski Lift', featureId: 3613, width: 'auto' },
		{ field: 'kbmMap', header: 'Balcony', featureId: 1948, width: 'auto' },
		{ field: 'kbmMap', header: 'Yard', featureId: 1927, width: 'auto' },
		{ field: 'kbmMap', header: 'Firepit', featureId: 1969, width: 'auto' },
		{ field: 'kbmMap', header: 'BBQ', featureId: 1966, width: 'auto' },
		{ field: 'kbmMap', header: 'Parking (Garage)', featureId: 2026, width: 'auto' },
		{ field: 'kbmMap', header: 'Parking (Avai)', featureId: 1996, width: 'auto' },
		{ field: 'kbmMap', header: 'Hot tub (Common)', featureId: 2119, width: 'auto' },
		{ field: 'kbmMap', header: 'Hot tub (Priv)', featureId: 1975, width: 'auto' },
		{ field: 'kbmMap', header: 'Pool (Priv)', featureId: 1951, width: 'auto' },
		{ field: 'kbmMap', header: 'Pool Heated', featureId: 2110, width: 'auto' },
		{ field: 'kbmMap', header: 'Condo', featureId: 2176, width: 'auto' },
		{ field: 'kbmMap', header: 'Resort', featureId: 2221, width: 'auto' },
		{ field: 'kbmMap', header: 'Townhome', featureId: 2233, width: 'auto' },
		{ field: 'kbmMap', header: 'Kitchen', featureId: 1303, width: 'auto' },
		{ field: 'kbmMap', header: 'Free Parking', featureId: 1996, width: 'auto' },
	];

	// Feature-only columns (skip first two: Property, Location)
	featureColumns: any[];

	constructor(
		private featurePivotSummaryService: FeaturesService,
		private alertService: AlertService,
		private propertyService: PropertyService,
		private propertyFeatureService: PropertyFeatureService,
		private constantService: ConstantService,
		private dialog: MatDialog,
		private cdr: ChangeDetectorRef,
	) {
		this.statuses = this.featurePivotSummaryService.statuses;
		this.featureColumns = this.columns.slice(2);
		this.constantService.locations.forEach(l => this.locationMap.set(l.locationId, l.name));
	}

	ngOnInit(): void {
		this.isSearching = true;
		// Load both APIs in parallel — table renders as soon as both complete
		forkJoin({
			propertiesRes: this.propertyService.findPropertySummaryData(),
			featuresRes: this.propertyService.getAllPropertyFeature(),
		}).pipe(first(), takeUntil(this.destroy$)).subscribe({
			next: ({ propertiesRes, featuresRes }) => {
				this.buildTableRows(propertiesRes.data.properties, featuresRes.data.propertyFeatures);
				this.isSearching = false;
				this.cdr.markForCheck();
			},
			error: (err) => {
				this.alertService.error(err);
				this.isSearching = false;
				this.cdr.markForCheck();
			}
		});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildTableRows(properties: any[], propertyFeatures: any[]) {
		// Build composite key map: `${shortTermRentalId}_${featureId}` → propertyFeature
		// Used for O(1) lookups in buildTableRows and O(1) updates in updatePropertyFeature
		this.pfLookup.clear();
		propertyFeatures.forEach(pf => {
			this.pfLookup.set(`${pf.shortTermRentalId}_${pf.featureId}`, pf);
		});

		this.tableRows = properties.map(property => {
			const featureMap = new Map<number, FeatureState>();
			this.featureColumns.forEach(col => {
				const pf = this.pfLookup.get(`${property.shortTermRentalId}_${col.featureId}`);
				featureMap.set(col.featureId, {
					exists: !!pf,
					status: pf ? !!pf.status : false,
				});
			});
			return { property, featureMap };
		});
	}

	updatePropertyFeature(property: any, featureId: number, status: any) {
		const key = `${property.shortTermRentalId}_${featureId}`;
		const existingPF = this.pfLookup.get(key);

		if (existingPF) {
			existingPF.status = status.checked;
			this.propertyFeatureService.updatePropertyFeatureStatus(existingPF)
				.pipe(first()).subscribe({
					error: (err) => this.alertService.error(err)
				});
		} else {
			const newPF: PropertyFeatureAdd = {
				featureId,
				shortTermRentalId: property.shortTermRentalId,
				status: status.checked,
				propertyFeatureDate: new Date(),
				name: '',
				title: ''
			};
			this.propertyFeatureService.addPropertyFeature(newPF)
				.pipe(first()).subscribe({
					next: (res: any) => {
						// Register in lookup so future toggles use the update path
						const saved = res?.data?.propertyFeature ?? { ...newPF };
						this.pfLookup.set(key, saved);
						const row = this.tableRows.find(r => r.property.shortTermRentalId === property.shortTermRentalId);
						const featureState = row?.featureMap.get(featureId);
						if (featureState) featureState.exists = true;
					},
					error: (err) => this.alertService.error(err)
				});
		}
	}

	/*********
	 * Copy To Feature
	 */
	copyToFeature(featureId: any) {
		this.isSearching = true;
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;

		this.featurePivotSummaryService.findById(featureId)
			.pipe(first()).subscribe({
				next: (res) => {
					this.isSearching = false;
					dialogConfig.data = { feature: res.data.feature };
					const dialogRef = this.dialog.open(FeatureAdminContainerComponent, dialogConfig);
					this.feature = res.data.feature;
					dialogRef.componentInstance.doSearchCB
						.pipe(takeUntil(this.destroy$))
						.subscribe(() => {
							// Refresh data instead of full page reload
							this.ngOnInit();
							dialogRef.close();
						});
				},
				error: (err) => {
					this.isSearching = false;
					this.alertService.error(err);
					this.cdr.markForCheck();
				}
			});

		this.addBodyClass();
	}

	addBodyClass() {
		document.body.classList.add('height_set');
	}
}
